import React from 'react';

import {
  getHeaderScheduleSummary,
  computeMeicTradeStats,
} from './MEICSupportStats';

import { useMeicSummary } from './useMeicSummary';

import '../../Styles/meicMonitor.css';

function toNumber(value) {
  if (value === null || value === undefined || value === '') return null;

  const number = Number(value);
  return Number.isNaN(number) ? null : number;
}

function formatNum(value, decimals = 2) {
  const number = toNumber(value);
  if (number === null) return '—';

  return number.toFixed(decimals);
}

function formatPnL(value) {
  const number = toNumber(value);
  if (number === null) return '—';

  return `${number > 0 ? '+' : ''}${number.toFixed(2)}`;
}

function pnlClass(value) {
  const number = toNumber(value);
  if (number === null) return '';

  return number < 0 ? 'negative' : 'positive';
}

function formatInt(value) {
  const number = toNumber(value);
  if (number === null) return '0';

  return String(Math.round(number));
}

function parseTimestamp(raw) {
  if (!raw) return null;
  if (raw instanceof Date) return raw;

  const str = String(raw).trim();

  if (str.includes('T')) {
    const date = new Date(str);
    return Number.isNaN(date.getTime()) ? null : date;
  }

  const [datePart, timeWithOffset] = str.split(' ');
  if (!datePart || !timeWithOffset) return null;

  const [yearStr, monthStr, dayStr] = datePart.split('-');
  const [timePart] = timeWithOffset.split('+');
  const [hourStr, minuteStr, secMsStr] = timePart.split(':');

  const year = Number(yearStr);
  const month = Number(monthStr);
  const day = Number(dayStr);
  const hours = Number(hourStr);
  const minutes = Number(minuteStr);

  let seconds = 0;
  let ms = 0;

  if (secMsStr) {
    const [secStr, msStrRaw] = secMsStr.split('.');
    seconds = Number(secStr);

    if (msStrRaw) {
      ms = Number(msStrRaw.padEnd(3, '0').slice(0, 3));
    }
  }

  const timestamp = Date.UTC(year, month - 1, day, hours, minutes, seconds, ms);
  const date = new Date(timestamp);

  return Number.isNaN(date.getTime()) ? null : date;
}

function formatTime(raw) {
  const date = parseTimestamp(raw);

  if (!date) return '—';

  return date.toLocaleTimeString([], {
    hour: '2-digit',
    minute: '2-digit',
  });
}

function parseTime(raw) {
  const date = parseTimestamp(raw);
  return date ? date.getTime() : 0;
}

function extractRegimeNumber(regimeLabel) {
  const match = String(regimeLabel || '').match(/(\d+)/);
  if (!match) return null;

  const number = Number(match[1]);
  return Number.isNaN(number) ? null : number;
}

function normalizeTradeForTable(trade) {
  return {
    id: String(trade.id),

    time: formatTime(trade.entryTime),
    sortTime: parseTime(trade.entryTime),

    side: trade.optionType || '—',
    short: formatNum(trade.shortStrike),
    long: formatNum(trade.longStrike),
    premium: formatNum(trade.premium),
    mark: trade.spreadMark != null ? formatNum(trade.spreadMark) : '—',
    pnl: formatPnL(trade.pnlDollars),

    status: trade.status || '—',
    outcome: trade.outcome || trade.reason || '—',

    raw: trade,
  };
}

function buildMeicEntries(trades) {
  const rows = (trades || [])
    .filter((trade) => trade && trade.id != null)
    .map(normalizeTradeForTable)
    .sort((a, b) => {
      if (a.sortTime && b.sortTime && a.sortTime !== b.sortTime) {
        return a.sortTime - b.sortTime;
      }

      const aId = Number(a.id);
      const bId = Number(b.id);

      if (Number.isFinite(aId) && Number.isFinite(bId)) {
        return aId - bId;
      }

      return String(a.id).localeCompare(String(b.id));
    });

  const groupedByTime = new Map();

  for (const row of rows) {
    const key = row.time || '—';

    if (!groupedByTime.has(key)) {
      groupedByTime.set(key, []);
    }

    groupedByTime.get(key).push(row);
  }

  return Array.from(groupedByTime.entries()).map(([time, legs]) => ({
    time,
    legs,
  }));
}

export default function MEICMonitor() {
  const {
    regime,
    regimeLabel,
    regimeTextTemplate,
    liveTradeRows,
    dailyTradesDate,
  } = useMeicSummary();

  const trades = liveTradeRows || [];
  const tradeStats = computeMeicTradeStats(trades);
  const meicEntries = buildMeicEntries(trades);

  const openCount = trades.filter((trade) => trade.status === 'OPEN').length;
  const totalEntries = meicEntries.length;

  const regimeNumber =
    typeof regime === 'number'
      ? regime
      : extractRegimeNumber(regimeLabel);

  const userTimeZone =
    typeof Intl !== 'undefined'
      ? Intl.DateTimeFormat().resolvedOptions().timeZone
      : 'America/New_York';

  const { localLabel } =
    regimeNumber !== null
      ? getHeaderScheduleSummary({
          regime: regimeNumber,
          tradingDate: dailyTradesDate,
          userTimeZone,
        })
      : { localLabel: '' };

  return (
    <section className="panel meicPanel">
      <div className="meicHeader">
        <div>
          <div className="meicEyebrow">Intraday Strategy Monitor</div>
          <h2>MEIC</h2>
          <p>
            Multiple Entry Iron Condor · Up to 6 entries per day
            {regimeTextTemplate ? ` · ${regimeTextTemplate}` : ''}
          </p>
        </div>

        <div className="meicBadges">
          <span className="meicRegimeBadge">{regimeLabel || 'Regime —'}</span>
          <span className="liveBadge">{openCount > 0 ? 'Active' : 'Flat'}</span>
        </div>
      </div>

      <div className="meicSummaryRow">
        <div>
          <span>Unrealized</span>
          <strong className={pnlClass(tradeStats.totalUnrealizedPnlDollars)}>
            {formatPnL(tradeStats.totalUnrealizedPnlDollars)}
          </strong>
        </div>

        <div>
          <span>Realized</span>
          <strong className={pnlClass(tradeStats.totalRealizedPnlDollars)}>
            {formatPnL(tradeStats.totalRealizedPnlDollars)}
          </strong>
        </div>

        <div>
          <span>Entries</span>
          <strong>{totalEntries} / 6</strong>
        </div>

        <div>
          <span>Wins</span>
          <strong>{formatInt(tradeStats.numWins)}</strong>
        </div>

        <div>
          <span>Losses</span>
          <strong>{formatInt(tradeStats.numLosses)}</strong>
        </div>

        <div>
          <span>Stops / Exp</span>
          <strong>
            {formatInt(tradeStats.numStops)} / {formatInt(tradeStats.numExpires)}
          </strong>
        </div>
      </div>

      <div className="meicEntryTimes">
        <span>Position Entry Times</span>
        <strong>{localLabel ? localLabel.replace('Position Entry Times: ', '') : '—'}</strong>
      </div>

      <div className="meicTableWrap">
        <table className="meicTable">
          <thead>
            <tr>
              <th>Time</th>
              <th>Side</th>
              <th>Short</th>
              <th>Long</th>
              <th>Premium</th>
              <th>Mark</th>
              <th>PnL</th>
              <th>Status</th>
              <th>Outcome</th>
            </tr>
          </thead>

          <tbody>
            {meicEntries.length > 0 ? (
              meicEntries.map((entry) =>
                entry.legs.map((leg, legIndex) => (
                  <tr
                    key={`${entry.time}-${leg.id}`}
                    className={legIndex === 0 ? 'meicEntryStart' : 'meicEntrySecondLeg'}
                  >
                    {legIndex === 0 && (
                      <td rowSpan={entry.legs.length} className="meicTimeCell">
                        {entry.time}
                      </td>
                    )}

                    <td>
                      <span className={`meicSidePill ${String(leg.side).toLowerCase()}`}>
                        {leg.side}
                      </span>
                    </td>
                    <td>{leg.short}</td>
                    <td>{leg.long}</td>
                    <td>{leg.premium}</td>
                    <td>{leg.mark}</td>
                    <td className={pnlClass(leg.raw.pnlDollars)}>{leg.pnl}</td>
                    <td>
                      <span className={`meicStatusPill ${String(leg.status).toLowerCase()}`}>
                        {leg.status}
                      </span>
                    </td>
                    <td>{leg.outcome}</td>
                  </tr>
                ))
              )
            ) : (
              <tr>
                <td colSpan="9" className="meicEmptyTrades">
                  No MEIC trades loaded for today.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </section>
  );
}